import { Icon, type IconName } from "@ui/components/Icon";
import { tabUnderlineClasses, tabUnderlineColor, tabUnderlineThickness } from "@ui/components/internal/TabUnderline";
import { accentScope } from "@ui/lib/accent-scope";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import type { PrefetchProps } from "@ui/lib/prefetch-context";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { Link } from "@ui/primitives/Link";

const subNavItem = tv({
  defaultVariants: {
    color: "neutral",
    isActive: false,
  },
  slots: {
    root: [
      "group relative flex flex-col items-center gap-1 px-2 pb-2.5 pt-1 no-underline outline-none",
      "rounded-lg focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-1",
      // Active underline indicator — shared mechanics with SectionTab; SubNav insets and rounds
      // the rule.
      tabUnderlineClasses,
      tabUnderlineThickness["0.5"],
      "after:inset-x-1 after:rounded-full",
    ],
    // Brand icons carry fixed colours, so active emphasis comes from opacity, not recolouring.
    media: "flex size-6 items-center justify-center transition-opacity",
    label: "max-w-full truncate text-center font-body text-xs font-medium leading-4 transition-colors",
  },
  variants: {
    // Tint per item — the accent rungs resolve against the `data-accent` scope opened below.
    color: {
      pink: { root: tabUnderlineColor.accent },
      green: { root: tabUnderlineColor.accent },
      orange: { root: tabUnderlineColor.accent },
      blue: { root: tabUnderlineColor.accent },
      neutral: { root: tabUnderlineColor["neutral-900"] },
    },
    isActive: {
      true: { root: "after:opacity-100", media: "opacity-100", label: "font-semibold text-neutral-900" },
      false: { media: "opacity-55", label: "text-neutral-500" },
    },
  },
});

export type SubNavItemColor = "pink" | "green" | "orange" | "blue" | "neutral";

interface SubNavItemBaseProps {
  label: string;
  icon: IconName;
  isActive?: boolean;
  /** Underline tint when active. */
  color?: SubNavItemColor;
}

/** Link mode (default) — renders a route `Link`; used by the persistent Explore sub-nav. */
type SubNavItemLinkProps = SubNavItemBaseProps & {
  href: string;
  onPress?: never;
} & Pick<PrefetchProps, "prefetch" | "cacheFor">;

/** Button mode — renders a `Button`; used by in-place tab switchers (e.g. the search overlay). */
type SubNavItemButtonProps = SubNavItemBaseProps & {
  onPress: () => void;
  href?: never;
};

export type SubNavItemProps = SubNavItemLinkProps | SubNavItemButtonProps;

/**
 * A single navigation tab inside `SubNav` — a sticker icons stacked above its label. In link mode
 * (`href`) it renders a route `Link` with `aria-current="page"`; in button mode (`onPress`) it
 * renders a `Button` for in-place tab switching (`aria-current` set, no navigation). Active state
 * is driven by the consumer via `isActive`, which reveals the underline indicator.
 *
 * Icons render filled in their brand colours at every state (matching the desktop `TopBarNavLink`);
 * the active tab is distinguished by a bolder, darker label, full icon opacity, and the underline.
 */
export function SubNavItem(props: SubNavItemProps) {
  const { label, icon, isActive = false, color = "neutral" } = props;
  const styles = subNavItem({ color, isActive });
  const inner = (
    <>
      <span aria-hidden="true" className={styles.media()}>
        <Icon name={icon} size="24" type="sticker" />
      </span>
      <span className={styles.label()}>{label}</span>
    </>
  );

  if (props.onPress) {
    return (
      <Button
        {...accentScope(color)}
        aria-current={isActive || undefined}
        className={styles.root()}
        onPress={props.onPress}
      >
        {inner}
      </Button>
    );
  }

  return (
    <Link
      {...accentScope(color)}
      href={props.href}
      prefetch={props.prefetch ?? "hover"}
      cacheFor={props.cacheFor ?? NAV_PREFETCH_CACHE_FOR}
      aria-current={isActive ? "page" : undefined}
      className={styles.root()}
    >
      {inner}
    </Link>
  );
}
